import { DialogComponent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

const CANADA_RACK_BAG_VARIANT_ID = 48408391123114;
const US_RACK_BAG_VARIANT_ID = 48408391188650;

const RACK_HANDLE_TEXT = 'mahjong-rack-set';

let isSyncingRackBags = false;

async function getCart() {
  const response = await fetch('/cart.js');
  return response.json();
}

async function changeCartItem(variantId, quantity) {
  await fetch('/cart/change.js', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify({
      id: String(variantId),
      quantity,
    }),
  });
}

async function addCartItem(variantId, quantity) {
  await fetch('/cart/add.js', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify({
      items: [
        {
          id: Number(variantId),
          quantity,
          properties: {
            _free_gift: 'Rack Bag',
          },
        },
      ],
    }),
  });
}

function getRackBagVariantIdForCart(cart) {
  const hasUSRack = cart.items.some((item) => item.handle?.includes(`${RACK_HANDLE_TEXT}-us`));

  return hasUSRack ? US_RACK_BAG_VARIANT_ID : CANADA_RACK_BAG_VARIANT_ID;
}

function countRackQuantity(cart) {
  return cart.items.reduce((total, item) => {
    const isRack = item.handle?.includes(RACK_HANDLE_TEXT);
    const isRackBag =
      item.variant_id === CANADA_RACK_BAG_VARIANT_ID ||
      item.variant_id === US_RACK_BAG_VARIANT_ID;

    if (isRack && !isRackBag) {
      return total + item.quantity;
    }

    return total;
  }, 0);
}

async function syncRackBags() {
  if (isSyncingRackBags) return;

  isSyncingRackBags = true;

  try {
    const cart = await getCart();

    const rackQuantity = countRackQuantity(cart);
    const targetBagVariantId = getRackBagVariantIdForCart(cart);

    const canadaBag = cart.items.find((item) => item.variant_id === CANADA_RACK_BAG_VARIANT_ID);
    const usBag = cart.items.find((item) => item.variant_id === US_RACK_BAG_VARIANT_ID);

    if (rackQuantity === 0) {
      if (canadaBag) await changeCartItem(CANADA_RACK_BAG_VARIANT_ID, 0);
      if (usBag) await changeCartItem(US_RACK_BAG_VARIANT_ID, 0);
      return;
    }

    const wrongBagVariantId =
      targetBagVariantId === US_RACK_BAG_VARIANT_ID ? CANADA_RACK_BAG_VARIANT_ID : US_RACK_BAG_VARIANT_ID;

    const wrongBag = cart.items.find((item) => item.variant_id === wrongBagVariantId);
    if (wrongBag) {
      await changeCartItem(wrongBagVariantId, 0);
    }

    const correctBag = cart.items.find((item) => item.variant_id === targetBagVariantId);

    if (correctBag) {
      if (correctBag.quantity !== rackQuantity) {
        await changeCartItem(targetBagVariantId, rackQuantity);
      }
    } else {
      await addCartItem(targetBagVariantId, rackQuantity);
    }

    document.dispatchEvent(new CartAddEvent());
  } catch (error) {
    console.error('Rack bag sync failed:', error);
  } finally {
    isSyncingRackBags = false;
  }
}

/**
 * A custom element that manages a cart drawer.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  connectedCallback() {
    document.addEventListener('click', this.#handleCheckoutClick, true);
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.addEventListener('change', this.#handleCartChange);
    syncRackBags();
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.#handleCheckoutClick, true);
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.removeEventListener('change', this.#handleCartChange);
  }

  #handleCartAdd = () => {
    syncRackBags();

    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
  };

  #handleCartChange = () => {
    syncRackBags();
  };

  #handleCheckoutClick = async (event) => {
    const checkoutButton = event.target.closest(
      'button[name="checkout"], a[href="/checkout"], [href*="/checkout"]'
    );

    if (!checkoutButton) return;

    event.preventDefault();
    event.stopPropagation();

    checkoutButton.setAttribute('disabled', 'disabled');
    checkoutButton.style.pointerEvents = 'none';

    await syncRackBags();

    window.location.href = '/checkout';
  };

  open() {
    this.showDialog();

    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}

